import { selectedReport as selectedReportAtom } from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { abyssColors } from '@/theme/colors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material';
import { useRouter } from '@/hooks';
import { MouseEvent, useState } from 'react';
import { useAtom } from 'jotai';
import {
  useDeleteReportMutation,
  AllReportsListDocument,
  ReportStatus,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

export const ReportMenu = () => {
  const [selectedReport, setSelectedReport] = useAtom(selectedReportAtom);
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const { showSnackBar } = useSnackBarMessage({});
  const router = useRouter();
  const structureId = useGetStructureId();

  const open = Boolean(anchorElement);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(null);
  };

  const [mutate, { loading }] = useDeleteReportMutation({
    variables: {
      reportId: selectedReport?.id ?? '',
    },
    refetchQueries: [AllReportsListDocument],
    onCompleted: () => {
      showSnackBar('Report has been deleted successfully', 'success');
      setSelectedReport(undefined);
      router.push(`/analysis/${structureId}/reports`);
    },
    onError: (error) => {
      const message = error?.message;
      showSnackBar(
        message || 'There was an error deleting the report. Please try again or contact support.',
        'error'
      );
    },
  });

  return (
    <Box>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon
          sx={{
            margin: 0,
            fontSize: '2.4rem',
            color: abyssColors.primary[500],
          }}
        />
      </IconButton>
      <Menu anchorEl={anchorElement} open={open} onClose={handleClose}>
        {selectedReport?.status === ReportStatus.Draft && (
          <MenuItem
            disabled={loading}
            onClick={() => {
              const result = window.confirm('Do you really want to delete this report?');
              if (result) {
                mutate();
              }
            }}
          >
            Delete report
            {loading && <CircularProgress size={20} />}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
